import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { taskActions, useTaskInvalidate, statusPill } from "../hooks/useTasks";
import type { TaskItem } from "../lib/types";

function formatTimestamp(ts?: string): string {
  if (!ts) return "";
  return new Date(ts + (ts.includes("Z") ? "" : "Z")).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function Pill({ task }: { task: TaskItem }) {
  const p = statusPill(task);
  if (!p) return null;
  return <span className={`task-status-pill ${p.cls}`}>{p.label}</span>;
}

export function TaskDetailModal({ task, onClose }: { task: TaskItem | null; onClose: () => void }) {
  const navigate = useNavigate();
  const invalidate = useTaskInvalidate();
  const actions = taskActions(invalidate);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (!task) return null;
  const t = task as TaskItem & Record<string, any>;
  const isEmailTask = t.source === "email-triage";
  const showDraft = t.draft_text && (t.status === "draft-ready" || t.status === "sent");

  const close = () => {
    setEditing(false);
    onClose();
  };

  async function approve() {
    if (!confirm("Send this draft email to the customer?")) return;
    try {
      if (editing) await actions.editDraft(t.id, draft);
      await actions.approve(t.id);
      close();
    } catch (e) {
      alert("Send failed: " + (e as Error).message);
    }
  }
  async function reject() {
    const reason = prompt("Rejection reason (optional):") || "";
    try {
      await actions.reject(t.id, reason);
      close();
    } catch (e) {
      alert("Error: " + (e as Error).message);
    }
  }
  async function saveDraft() {
    try {
      await actions.editDraft(t.id, draft);
      t.draft_text = draft;
      setEditing(false);
    } catch (e) {
      alert("Save failed: " + (e as Error).message);
    }
  }
  async function markDone() {
    try {
      await actions.done(t.id);
      close();
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      className="task-detail-modal show"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="task-detail-modal-content">
        <span className="close" onClick={close}>
          &times;
        </span>
        <div id="taskDetailBody">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div className="task-detail-field-label" style={{ margin: 0, fontSize: 13 }}>
              {isEmailTask ? "Email Investigation" : "Task"}
            </div>
            <Pill task={t} />
          </div>
          <div className="task-detail-field">
            <div className="task-detail-field-label">Customer</div>
            <div className="task-detail-field-value">
              {t.customer_id ? (
                <a
                  className="task-detail-customer-link"
                  onClick={() => {
                    close();
                    navigate({ to: "/users/$customerId", params: { customerId: String(t.customer_id) } });
                  }}
                >
                  {t.customer_name || "Unknown"}
                </a>
              ) : (
                <>
                  <span>{t.customer_name || "Unknown"}</span>
                  <span className="task-detail-not-in-db">(not in db)</span>
                </>
              )}
            </div>
            {t.customer_email && (
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{t.customer_email}</div>
            )}
          </div>

          {t.invoice_id && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Invoice</div>
              <div className="task-detail-field-value">
                <a href={`https://dashboard.stripe.com/invoices/${t.invoice_id}`} target="_blank" rel="noreferrer" style={{ color: "#37358F" }}>
                  {t.invoice_id}
                </a>
              </div>
            </div>
          )}
          {t.gmail_thread_id && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Gmail Thread</div>
              <div className="task-detail-field-value">
                <a href={`https://mail.google.com/mail/u/0/#inbox/${t.gmail_thread_id}`} target="_blank" rel="noreferrer" className="task-gmail-link">
                  Open in Gmail &rarr;
                </a>
              </div>
            </div>
          )}
          {t.task_note && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Summary</div>
              <div className="task-detail-field-value" style={{ whiteSpace: "pre-wrap" }}>
                {t.task_note}
              </div>
            </div>
          )}
          {showDraft && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Draft Response</div>
              {editing ? (
                <textarea className="task-draft-edit" value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus />
              ) : (
                <div className="task-draft-preview">{t.draft_text}</div>
              )}
            </div>
          )}
          {t.agent_question && (t.status === "blocked" || t.status === "awaiting-holly") && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Blocker</div>
              <div className="task-agent-question">{t.agent_question}</div>
            </div>
          )}
          {t.email_content && (
            <div className="task-detail-field">
              <div className="task-detail-field-label">Original Email</div>
              <div className="task-detail-email-content">{t.email_content}</div>
            </div>
          )}
          {t.investigation_log && (
            <div className="task-detail-field">
              <LogToggle log={t.investigation_log} />
            </div>
          )}
          <div className="task-detail-field">
            <div className="task-detail-field-label">Timeline</div>
            <div className="task-timeline">
              {t.created_at && <div className="task-timeline-item"><span className="task-timeline-dot active" />Created: {formatTimestamp(t.created_at)}</div>}
              {t.processed_at && <div className="task-timeline-item"><span className="task-timeline-dot active" />Processed: {formatTimestamp(t.processed_at)}</div>}
              {t.approved_at && <div className="task-timeline-item"><span className="task-timeline-dot active" />Approved: {formatTimestamp(t.approved_at)}</div>}
              {t.sent_at && <div className="task-timeline-item"><span className="task-timeline-dot active" />Sent: {formatTimestamp(t.sent_at)}</div>}
            </div>
          </div>
        </div>

        <div className="task-detail-actions" id="taskDetailActions">
          {isEmailTask && t.status === "draft-ready" ? (
            <>
              <button className="task-btn-reject" onClick={reject}>Reject</button>
              {editing ? (
                <button className="task-btn-edit" onClick={saveDraft}>Save Draft</button>
              ) : (
                <button
                  className="task-btn-edit"
                  onClick={() => {
                    setDraft(t.draft_text || "");
                    setEditing(true);
                  }}
                >
                  Edit Draft
                </button>
              )}
              <button className="task-btn-approve" onClick={approve}>Approve &amp; Send</button>
            </>
          ) : isEmailTask && (t.status === "blocked" || t.status === "awaiting-holly") ? (
            <button className="task-btn-reject" onClick={reject}>Dismiss</button>
          ) : (
            <button className="submit-btn" onClick={markDone}>Mark Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

function LogToggle({ log }: { log: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="task-log-toggle" onClick={() => setOpen((v) => !v)}>
        Show/Hide Investigation Log
      </button>
      <div className="task-investigation-log" style={{ display: open ? "block" : "none" }}>
        {log}
      </div>
    </>
  );
}
